import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Pencil, Plus, Search } from 'lucide-react';
import { useWorkspacesInfinite } from '@/api/workspaces';
import type { WorkspaceStatus, WorkspaceWithStats } from '@/api/types';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Select, Spinner } from '@/components/ui';
import { Can } from '@/app/auth/AuthContext';
import { useAuth } from '@/app/auth/useAuth';
import { formatRelative, humanize } from '@/lib/format';
import { ClientFormModal } from './ClientFormModal';
import { EditClientModal } from './EditClientModal';
import { PLAN_TONE, STATUS_TONE } from './meta';

/**
 * Platform console (super admin): every client of the support platform, each with its own
 * isolated workspace. Create a client with its owner, change plan, suspend, or open the workspace.
 */
export function ClientsPage() {
  const { enterWorkspace } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<WorkspaceStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<WorkspaceWithStats | null>(null);

  const list = useWorkspacesInfinite({ q: useDeferredValue(q.trim()), status });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Platform"
        title="Clients"
        description="Each client gets an isolated workspace: its own agents, customers, tickets, channels and AI. Open one to work inside it."
        actions={
          <Can I="create" a="Workspace">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New client
            </Button>
          </Can>
        }
      />

      <Card>
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-end">
          <label className="relative flex-1">
            <span className="sr-only">Search clients</span>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, handle or contact email…"
              className="focus:border-brand-500 focus:ring-brand-500/30 h-10 w-full rounded-lg border border-slate-200 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
            />
          </label>
          <Select
            aria-label="Filter by status"
            value={status}
            onChange={(e) => setStatus(e.target.value as WorkspaceStatus | '')}
            wrapperClassName="sm:w-44"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </Select>
        </div>

        {list.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" label="Loading clients" />
          </div>
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title={q || status ? 'No clients match' : 'No clients yet'}
            description={
              q || status
                ? 'Try a different search or status.'
                : 'Create your first client to give them a workspace.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr className="border-b border-slate-100">
                    <th scope="col" className="px-4 py-3">
                      Client
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Plan
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">
                      Team
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">
                      Open tickets
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">
                      Customers
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Created
                    </th>
                    <th scope="col" className="px-4 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((w) => (
                    <tr key={w.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/clients/${w.id}`}
                          className="hover:text-brand-700 font-medium text-slate-900"
                        >
                          {w.name}
                        </Link>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                          <span className="font-mono">{w.slug}</span>
                          {w.contactEmail && <span className="truncate">· {w.contactEmail}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <Badge tone={PLAN_TONE[w.plan]}>{humanize(w.plan)}</Badge>
                          {w.status !== 'active' && (
                            <Badge tone={STATUS_TONE[w.status]}>{humanize(w.status)}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 tabular-nums">{w.stats.users}</td>
                      <td className="px-4 py-3 text-right text-slate-700 tabular-nums">
                        {w.stats.openTickets}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 tabular-nums">
                        {w.stats.customers}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">
                        {formatRelative(w.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(w)} className="mr-1">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => enterWorkspace(w.id)}>
                          Open workspace <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="flex justify-center border-t border-slate-100 p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {creating && <ClientFormModal onClose={() => setCreating(false)} />}
      {editing && <EditClientModal client={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
